import { Component, OnInit,OnDestroy, TemplateRef, ViewChild } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import * as pluginDataLabels from "chartjs-plugin-datalabels";
import { DashboardData } from "../survey/survey-new-dashboard/get-dashboard-data-model";
import { SurveyService } from "../survey/survey.service";
import { ChartDataSets, ChartOptions, ChartType } from "chart.js";
import { Color, Label } from "ng2-charts";
import { MatDialog } from "@angular/material/dialog";
import { MatTable } from "@angular/material/table";
import { DownloadDashboard } from "../reports/ppt-generation/download-dashboard";
import { DownloadEngagementDashboard } from "../reports/ppt-generation/download-engagement-dashboard";
import { ChatbotWidgetService } from '../shared/chatbot-widget.service';
import { CdkDragDrop, moveItemInArray } from "@angular/cdk/drag-drop";

interface CustomChartDataSets extends ChartDataSets {
  percentages?: string[]; // Optional property for percentages
}
@Component({
  selector: "app-external-user-dashboard",
  templateUrl: "./external-user-dashboard.component.html",
  styleUrls: ["./external-user-dashboard.component.css"],
})
export class ExternalUserDashboardComponent implements OnInit, OnDestroy {
  displayedColumns = [];
  tableData: any[] = [];
  surveyUid: string;
  getPartipantExcelResponse: any;
  getDashboardResponse: any;
  isSurveyDashboard: any;
  isDownloading: boolean = false;
  data: any;
  public dashboardData = new DashboardData();
  totalData: any;
  selectedLabel: string;
  showNextDashboard: boolean;
  otherFiltersNote: string = "";
  isSuppressed: boolean = false;
  suppressionMessage: string = "";

  // --- Field / demographic filter state (participation dashboard) ---
  selectedFieldLabels: string[] = ["All"]; // multi-select model for select-1 (field labels)
  activeFieldLabel: string = "All"; // field currently shown/edited in select-2
  activeFieldOptions: string[] = []; // options for select-2
  activeFieldSelectedValues: string[] = []; // currently staged selection in select-2
  demographicFilters: { fieldName: string; fieldValue: string[] }[] = [];
  // Applied filters (shown as chips)
  appliedDemographicFilters: { fieldName: string; fieldValue: string[] }[] = [];
  fieldOptionsCache: { [key: string]: string[] } = {};
  availableDemographicFilters: any;
  private previousSelectedFields: string[] = ["All"];
  // Client-side row reordering only - external viewers have no authenticated
  // session, so the reordered layout can't be persisted like on the internal
  // dashboard; it just lets this viewer temporarily reorder their own view.
  sortByTotal: boolean = false;
  @ViewChild('table') table: MatTable<any>;
  public pieChartLabels: string[] = ["Pending", "Completed"];
  public chartColors: any[] = [
    {
      backgroundColor: ["#e0542c", "#70c4fc"],
      borderColor: ["#FFF", "#FFF"],
      borderWidth: 1,
    },
  ];

  public pieChartData: any;
  public pieChartType: string = "pie";
  allDashboard: boolean = false;
  public pieChartOptions: ChartOptions = {
    responsive: true,
    plugins: {
      legend: {
        display: true,
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            let label = context.label || "";
            let value = context.raw;
            return `${label}: ${value}%`;
          },
        },
      },
      datalabels: {
        formatter: (value, ctx) => {
          return `${value}%`;
        },
        color: "#444",
        font: {
          weight: "bold",
          size: 14,
        },
      },
    },
  };

  public pieChartPlugins = [pluginDataLabels];

  topBarChartData: any[] = [];
  bottomBarChartData: any[] = [];

  barChartOptions: ChartOptions = {
    responsive: true,
    scales: {
      xAxes: [
        {
          gridLines: { display: false },
          ticks: { beginAtZero: true },
        },
      ],
      yAxes: [
        {
          gridLines: { display: true },
          ticks: {
            beginAtZero: true, // Ensures Y-axis starts from 0
            max: 100, // Limits Y-axis to a maximum value of 100
          },
          stacked: true, // Keeps stacked bars if you're using stacked chart
        },
      ],
    },
    plugins: {
      datalabels: {
        color: "white",
        formatter: (value, context) => {
          const dataset = context.dataset as CustomChartDataSets; // Cast dataset to include percentages
          const percentage = dataset.percentages
            ? dataset.percentages[context.dataIndex]
            : "";
          return percentage === "0%" ? "" : percentage; // Hide 0% labels
        }, // Change text color to white
        font: {
          weight: "bold",
          size: 12, // Customize font size if necessary
        },
        align: "center", // Align the label to the center
        anchor: "center", // Anchor the label in the middle of the bar
      },
    },
  };
  barChartLabels: string[] = [
    "column1",
    "column2",
    "column3",
    "column4",
    "column5",
    "column6",
  ];
  barChartData: CustomChartDataSets[] = [];
  barChartType: ChartType = "bar";
  barChartLegend = true;
  barChartColors: Color[] = [
    { backgroundColor: "#807c7c" },
    { backgroundColor: "#a06444" },
    { backgroundColor: "#ecbc0c" },
    { backgroundColor: "#588ccc" },
    { backgroundColor: "#ff8404" },
  ];

  responseBars = [
    {
      class: "strongly-disagree",
      key: "stronglyDisagree",
      title: "Strongly Disagree: ",
    },
    { class: "disagree", key: "disagree", title: "Disagree: " },
    {
      class: "neither-aod",
      key: "neitherAOD",
      title: "Neither Agree nor Disagree: ",
    },
    { class: "almost-agree", key: "agree", title: "Agree: " },
    {
      class: "strongly-agree",
      key: "stronglyAgree",
      title: "Strongly Agree: ",
    },
  ];

  dashboardRequest: {
    surveyUId: string;
    formFields: {
      index: number;
      label: string;
      usedForFilter: boolean;
      options: any[];
    }[];
    demographicFilters?: { fieldName: string; fieldValue: string[] }[];
  };
  dashboardLinkId: string;
  engagementDashboardLinkId: string;
  getEngagementDashboardResponse: any;
  invalidLink: boolean = false;
  getSurveyStatementResponse: any;
  getExcelResponse: any;
  // Engagement Table tab data (same shape as getEngagementDashboardResponse,
  // kept separate so drag reordering can update it without touching the
  // chart-driving response directly).
  engagementTableResponse: any = null;

  showPasswordDialog = false;
  password = "";
  passwordError = "";
  isPasswordVerified = false;

  // Sentinel value for the "Current View (as displayed)" dropdown option.
  currentViewOption = "__CURRENT__";
  // Default the download dropdown to "Current View" so a plain download matches
  // the on-screen view; users can still pick specific filters instead.
  selectedFilterLabels: any = ["__CURRENT__"];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private surveyService: SurveyService,
    private dialog: MatDialog,
    private pptParticipationService: DownloadDashboard,
    private pptService: DownloadEngagementDashboard,
    private chatbot: ChatbotWidgetService
  ) {}

  ngOnInit() {
    this.chatbot.load(true, true);
    this.route.paramMap.subscribe((params) => {
      if (params.get("dashboardLinkId")) {
        this.dashboardLinkId = params.get("dashboardLinkId");
        this.isSurveyDashboard = true;
         this.showPasswordDialog = true;
        // this.getDashBoard(this.dashboardLinkId);
        this.displayedColumns = [
          "no",
          "level",
          "completion-rate",
          "pending",
          "completed",
          "total",
        ];
      } else if (params.get("engagementDashboardLinkId")) {
        this.engagementDashboardLinkId = params.get(
          "engagementDashboardLinkId",
        );
        this.isSurveyDashboard = false;

        this.showPasswordDialog = true;
        // this.getDashBoardEngagementScoreDashBoard(this.engagementDashboardLinkId);
        // this.getEngagementDashBoard(this.engagementDashboardLinkId);
      }
    });
  }

   ngOnDestroy() {
    this.chatbot.destroy();
  }

  clearFilter() {
    this.resetToAll();
  }

  drop(event: CdkDragDrop<any[]>) {
    if (event.previousIndex === event.currentIndex) {
      return;
    }
    moveItemInArray(this.tableData, event.previousIndex, event.currentIndex);
    // On the internal dashboard, every drop persists to the server and then
    // re-fetches, which hands mat-table a brand-new array of brand-new row
    // objects - that's what actually resets mat-table's row recycling on
    // every single drag, not just the first. External viewers have no
    // session to persist through, so replicate the same "fresh objects"
    // effect locally by cloning each row instead of reusing references.
    this.tableData = this.tableData.map((row) => ({ ...row }));
    // Reordering rows is itself a custom sort - reflect that in the toggle
    // immediately rather than waiting for the user to flip it manually.
    this.sortByTotal = true;
    if (this.table) {
      this.table.renderRows();
    }
    this.persistCustomSort(
      this.tableData.map((row) => row.filter),
      true,
    );
  }

  onSortByTotalChange(event: any) {
    if (this.activeFieldLabel === "All") {
      this.sortByTotal = false;
      return;
    }
    if (!event.checked) {
      // defaultTableData is just whatever the server returned on load - if
      // the field's persisted order was ALREADY custom at that point (e.g.
      // set from the internal admin dashboard), that snapshot is custom
      // too, not a true default. Compute the real fallback order (the same
      // one the backend uses when UseCustomOptionOrder is false) instead.
      this.tableData = this.sortByTotalDescending(this.tableData);
      if (this.table) {
        this.table.renderRows();
      }
    }
    this.persistCustomSort(
      this.tableData.map((row) => row.filter),
      event.checked,
    );
  }

  // Mirrors the backend's fallback sort (CalculateDashboardForField /
  // CalculateEngagementForField) used whenever a field has no custom order:
  // "Others" always last, then by Total descending.
  private sortByTotalDescending<T extends { filter?: string; total?: number }>(
    data: T[],
  ): T[] {
    return [...data].sort((a, b) => {
      const aOthers = a.filter === "Others" ? 1 : 0;
      const bOthers = b.filter === "Others" ? 1 : 0;
      if (aOthers !== bOthers) {
        return aOthers - bOthers;
      }
      return (b.total || 0) - (a.total || 0);
    });
  }

  // Persists the custom sort order to the database, authorized by the
  // share link's own password (the same gate GetDashBoardByLink already
  // uses) instead of the admin JWT UpdatePartipantExcelInfoBySurveyUId
  // requires. This is what makes the order consistent across every future
  // viewer and PPT/Excel downloads, instead of resetting per-browser-session.
  private persistCustomSort(options: string[], useCustomOptionOrder: boolean) {
    if (
      !this.dashboardLinkId ||
      !this.activeFieldLabel ||
      this.activeFieldLabel === "All"
    ) {
      return;
    }

    // Excludes the synthetic "Others" aggregate (small categories merged
    // below the reporting threshold - see otherFiltersNote), which isn't a
    // real field value. Any real category temporarily missing here because
    // it's currently grouped gets auto-re-added server-side (the "add
    // missing options" step inside UpdatePartipantExcelInfoBySurveyUId runs
    // before the save), so nothing is lost - only "Others" itself must
    // never be written back as if it were a genuine option.
    const realOptions = options.filter((o) => o !== "Others");

    const payload = {
      dashboardLinkId: this.dashboardLinkId,
      password: this.password,
      fieldLabel: this.activeFieldLabel,
      options: realOptions,
      useCustomOptionOrder,
    };

    this.surveyService.updateDashboardCustomSortByLink(payload).subscribe(
      () => {},
      (err) => console.log(err),
    );
  }

  clearFilter1() {
    this.resetToAll1();
  }

  drop1(event: CdkDragDrop<any[]>) {
    if (
      !this.engagementTableResponse ||
      event.previousIndex === event.currentIndex
    ) {
      return;
    }
    moveItemInArray(
      this.engagementTableResponse.engagementDatas,
      event.previousIndex,
      event.currentIndex,
    );
    // Fresh object clones, same reasoning as the participation table's drop():
    // avoids stale row-recycling state on repeated drags.
    this.engagementTableResponse.engagementDatas =
      this.engagementTableResponse.engagementDatas.map((row) => ({ ...row }));
    this.sortByTotal = true;
    // Keep the chart in sync with the reordered table.
    this.buildEngagementBarChart(this.engagementTableResponse.engagementDatas);
    this.persistEngagementCustomSort(
      this.engagementTableResponse.engagementDatas.map((row) => row.filter),
      true,
    );
  }

  onSortByTotalChange1(event: any) {
    if (this.activeFieldLabel === "All") {
      this.sortByTotal = false;
      return;
    }
    if (!event.checked && this.engagementTableResponse) {
      // See onSortByTotalChange's comment: compute the real fallback order
      // rather than trusting defaultEngagementTableData, which may already
      // be custom-sorted if that was the field's persisted state on load.
      this.engagementTableResponse.engagementDatas = this.sortByTotalDescending(
        this.engagementTableResponse.engagementDatas,
      );
      this.buildEngagementBarChart(this.engagementTableResponse.engagementDatas);
    }
    if (this.engagementTableResponse) {
      this.persistEngagementCustomSort(
        this.engagementTableResponse.engagementDatas.map((row) => row.filter),
        event.checked,
      );
    }
  }

  private persistEngagementCustomSort(
    options: string[],
    useCustomOptionOrder: boolean,
  ) {
    if (
      !this.engagementDashboardLinkId ||
      !this.activeFieldLabel ||
      this.activeFieldLabel === "All"
    ) {
      return;
    }

    // See persistCustomSort's comment: excludes the synthetic "Others"
    // aggregate row - any real category temporarily missing because it's
    // currently grouped gets auto-re-added server-side before the save.
    const realOptions = options.filter((o) => o !== "Others");

    const payload = {
      dashboardLinkId: this.engagementDashboardLinkId,
      password: this.password,
      fieldLabel: this.activeFieldLabel,
      options: realOptions,
      useCustomOptionOrder,
    };

    this.surveyService
      .updateEngagementDashboardCustomSortByLink(payload)
      .subscribe(
        () => {},
        (err) => console.log(err),
      );
  }

  getDashBoard() {
    this.chatbot.show();
    const payload = {
      dashboardLinkId: this.dashboardLinkId,
      password: this.password,
    };
    this.surveyService.getDashBoardByLink(payload).subscribe(
      (response) => {
        this.getPartipantExcelResponse = response;
        this.surveyUid = this.getPartipantExcelResponse.surveyUId;
        this.getPartipantExcelResponse.formFields.push({
          index: 0,
          label: "All",
          usedForFilter: true,
          options: [],
        });

        this.selectedLabel = "All";
        this.selectedFieldLabels = ["All"];
        this.activeFieldLabel = "All";
        this.activeFieldOptions = [];
        this.activeFieldSelectedValues = [];
        this.demographicFilters = [];
        this.appliedDemographicFilters = [];
        this.previousSelectedFields = ["All"];

        if (this.getPartipantExcelResponse.surveyUId !== null) {
          if (this.getPartipantExcelResponse.formFields.length > 0) {
            this.showNextDashboard = true;
          }
          this.dashboardRequest = {
            surveyUId: this.getPartipantExcelResponse.surveyUId,
            formFields: [
              {
                index: 0,
                label: "All",
                usedForFilter: true,
                options: [],
              },
            ],
          };
          this.getDashboardData(this.dashboardRequest);
          this.getAvailableFilters();
        }
      },
      (error) => {
        this.invalidLink = true;
        console.log(error);
      },
    );
  }

  getDashboardData(obj) {
    this.surveyService.getDashboardWithFiltersForSurvey(obj).subscribe(
      (response: any) => {
        // Response wraps the dashboard in dashboardModels[0] and carries an
        // Others-grouping note, same shape as the internal dashboard.
        this.getDashboardResponse =
          (response.dashboardModels && response.dashboardModels[0]) || {};

        this.otherFiltersNote = response.otherFilters || "";

        this.isSuppressed = !!response.isSuppressed;
        this.suppressionMessage =
          response.message ||
          "Sample size is too small to display this dashboard.";

        if (this.isSuppressed) {
          this.tableData = [];
          this.pieChartData = [0, 0];
          return;
        }

        this.tableData = this.getDashboardResponse.data;
        // Reflect whether this field already has a persisted custom order
        // (e.g. set earlier from the internal admin dashboard, or from a
        // previous external session) instead of always showing "off".
        const activeField = (
          (this.getPartipantExcelResponse &&
            this.getPartipantExcelResponse.formFields) ||
          []
        ).find((f) => f.label === this.activeFieldLabel);
        this.sortByTotal = !!(activeField && activeField.useCustomOptionOrder);
        this.pieChartData = [
          this.getDashboardResponse.total.pendingPercent,
          this.getDashboardResponse.total.completedPercent,
        ];
        this.allDashboard = this.getDashboardResponse.data.length === 0;
      },
      (error) => {
        this.invalidLink = true;
        console.log(error);
      },
    );
  }

  /**
   * Handles selection changes on select-1 (the multi-select of field labels).
   * Mirrors the internal dashboard's cascading filter behaviour: selecting
   * "All" resets everything, a newly added label becomes active and its
   * values are loaded into select-2.
   */
  onFieldSelectionChange(event: any) {
    let selectedFields = [...(event.value || [])];

    if (selectedFields.length > 1 && selectedFields.includes("All")) {
      selectedFields = selectedFields.filter((x) => x !== "All");
    }

    if (selectedFields.length === 1 && selectedFields[0] === "All") {
      this.previousSelectedFields = ["All"];
      this.resetToAll();
      return;
    }

    const existingFilters = [...this.demographicFilters];

    this.demographicFilters = selectedFields.map((label) => {
      const existing = existingFilters.find((x) => x.fieldName === label);

      return existing
        ? existing
        : {
            fieldName: label,
            fieldValue: [],
          };
    });

    this.selectedFieldLabels = [...selectedFields];

    if (this.selectedFieldLabels.length === 0) {
      this.previousSelectedFields = ["All"];

      this.selectedFieldLabels = ["All"];
      this.activeFieldLabel = "All";
      this.activeFieldOptions = [];
      this.activeFieldSelectedValues = [];
      this.demographicFilters = [];

      return;
    }

    const newlyAdded = selectedFields.find(
      (x) => !this.previousSelectedFields.includes(x),
    );

    const activeLabel =
      newlyAdded ||
      (selectedFields.includes(this.activeFieldLabel)
        ? this.activeFieldLabel
        : selectedFields[selectedFields.length - 1]);

    this.previousSelectedFields = [...selectedFields];

    this.activeFieldLabel = activeLabel;

    this.setActiveField(activeLabel);
  }

  /**
   * Handles selection changes on select-2 (values for the active field).
   * Only stages the values - the API call happens on "Apply Filter".
   */
  onValueSelectionChange(event: any) {
    if (!this.activeFieldLabel || this.activeFieldLabel === "All") {
      return;
    }

    this.activeFieldSelectedValues = [...event.value];

    const filter = this.demographicFilters.find(
      (x) => x.fieldName === this.activeFieldLabel,
    );

    if (filter) {
      filter.fieldValue = [...event.value];
    }
  }

  /**
   * Removes a field's chip entirely and falls back to the next active field
   * (or "All" if none remain).
   */
  removeDemographicFilter(filter: any) {
    this.demographicFilters = this.demographicFilters.filter(
      (x) => x.fieldName !== filter.fieldName,
    );

    this.appliedDemographicFilters = this.appliedDemographicFilters.filter(
      (x) => x.fieldName !== filter.fieldName,
    );

    this.selectedFieldLabels = this.selectedFieldLabels.filter(
      (x) => x !== filter.fieldName,
    );

    this.previousSelectedFields = [...this.selectedFieldLabels];

    if (this.selectedFieldLabels.length === 0) {
      this.resetToAll();

      return;
    }

    const nextField =
      this.selectedFieldLabels[this.selectedFieldLabels.length - 1];

    this.setActiveField(nextField);

    this.applyDemographicFilters();
  }

  /**
   * Sends the accumulated demographicFilters (plus the currently active
   * field object) to the dashboard API.
   */
  applyDemographicFilters() {
    if (!this.activeFieldLabel || this.activeFieldLabel === "All") {
      return;
    }

    const currentField = this.activeFieldLabel;

    const activeFieldObject = this.getPartipantExcelResponse.formFields.find(
      (x) => x.label === currentField,
    );

    if (!activeFieldObject) {
      return;
    }

    const filters = this.demographicFilters
      .filter((x) => x.fieldValue && x.fieldValue.length > 0)
      .map((x) => ({
        fieldName: x.fieldName,
        fieldValue: [...x.fieldValue],
      }));

    this.appliedDemographicFilters = JSON.parse(JSON.stringify(filters));

    this.dashboardRequest = {
      surveyUId: this.surveyUid,
      formFields: [{ ...activeFieldObject }],
      demographicFilters: filters,
    };

    this.getDashboardData(this.dashboardRequest);

    const filtersForOptions = filters.filter(
      (x) => x.fieldName !== currentField,
    );

    this.getAvailableFilters(filtersForOptions);

    // Restore current field after API finishes
    setTimeout(() => {
      this.setActiveField(currentField);
    });
  }

  private setActiveField(label: string) {
    this.activeFieldLabel = label;

    const filter = this.demographicFilters.find((x) => x.fieldName === label);

    this.activeFieldSelectedValues = filter ? [...filter.fieldValue] : [];
    // Field-specific custom order isn't persisted for external viewers,
    // so drop back to the default order whenever the active field changes.
    this.sortByTotal = false;

    const apiField = (this.availableDemographicFilters || []).find(
      (x) => x.fieldLabel === label,
    );

    if (apiField) {
      this.activeFieldOptions = [...apiField.availableValues];

      this.fieldOptionsCache[label] = [...apiField.availableValues];
    } else {
      // API no longer returns this field because it is already applied.
      // Restore from cache.
      this.activeFieldOptions = this.fieldOptionsCache[label]
        ? [...this.fieldOptionsCache[label]]
        : [];
    }
  }

  private resetToAll() {
    this.selectedFieldLabels = ["All"];
    this.activeFieldLabel = "All";
    this.activeFieldOptions = [];
    this.activeFieldSelectedValues = [];
    this.demographicFilters = [];
    this.appliedDemographicFilters = [];
    this.sortByTotal = false;

    const allObject = this.getPartipantExcelResponse.formFields.find(
      (f) => f.label === "All",
    );

    this.dashboardRequest = {
      surveyUId: this.surveyUid,
      formFields: [allObject],
    };

    this.getDashboardData(this.dashboardRequest);

    this.getAvailableFilters();
  }

  getAvailableFilters(appliedFilters: any[] = []) {
    const payload = {
      surveyUId: this.surveyUid,
      appliedFilters,
    };

    this.surveyService
      .getAvailableDemographicFilters(payload)
      .subscribe((res: any) => {
        this.availableDemographicFilters = res || [];

        this.availableDemographicFilters.forEach((field) => {
          this.fieldOptionsCache[field.fieldLabel] = [...field.availableValues];
        });
      });
  }

  getEngagementDashBoard() {
    this.chatbot.show();
    const payload = {
      dashboardLinkId: this.engagementDashboardLinkId,
      password: this.password,
    };
    this.surveyService
      .getEngagementDashBoardByLink(payload)
      .subscribe((response) => {
        this.getExcelResponse = response;
        this.dashboardRequest = {
          surveyUId: this.getExcelResponse.surveyUId,
          formFields: [
            {
              index: 0,
              label: "All",
              usedForFilter: true,
              options: [],
            },
          ],
        };
        this.surveyUid = this.getExcelResponse.surveyUId;
        this.getExcelResponse.formFields.push({
          index: 0,
          label: "All",
          usedForFilter: true,
          options: [],
        });

        this.selectedLabel = "All";
        this.selectedFieldLabels = ["All"];
        this.activeFieldLabel = "All";
        this.activeFieldOptions = [];
        this.activeFieldSelectedValues = [];
        this.demographicFilters = [];
        this.appliedDemographicFilters = [];
        this.previousSelectedFields = ["All"];

        this.getDashBoardEngagementScoreDashBoard(this.dashboardRequest);
        this.getSurveyStatements(this.surveyUid);
        this.getAvailableFilters1();
      });
  }

  getSurveyStatements(surveyuid: string) {
    this.surveyService
      .getSurveyResponseStatement(surveyuid)
      .subscribe((response) => {
        this.getSurveyStatementResponse = response;

        // Map and prepare data for top statements
        if (this.getSurveyStatementResponse.topStatement.length > 0) {
          this.topBarChartData =
            this.getSurveyStatementResponse.topStatement.map((statement) =>
              this.mapToChartData(statement),
            );
        }

        // Map and prepare data for bottom statements
        if (this.getSurveyStatementResponse.bottomStatement.length > 0) {
          this.bottomBarChartData =
            this.getSurveyStatementResponse.bottomStatement.map((statement) =>
              this.mapToChartData(statement),
            );
        }
      });
  }

  mapToChartData(statement) {
    // statement.stronglyAgree/agree/etc. are already percentages (0-100) computed server-side by
    // ParticipantServices - dividing by totalResponses again here was dividing an already-percent
    // value by the respondent count a second time, silently producing the wrong number (masked
    // because the chart only cares about relative proportions between slices, not the absolute
    // values, so it never visibly broke).
    return [
      {
        value: statement.stronglyAgree,
        color: "#807c7c",
      },
      {
        value: statement.agree,
        color: "#a06444",
      },
      {
        value: statement.neitherAOD,
        color: "#ecbc0c",
      },
      {
        value: statement.disagree,
        color: "#588ccc",
      },
      {
        value: statement.stronglyDisagree,
        color: "#ff8404",
      },
    ].filter((point) => point.value > 0); // Exclude fields with 0 value
  }

  /**
   * Engagement dashboard's select-1 handler (mirrors onFieldSelectionChange,
   * scoped to the engagement branch's getExcelResponse/dashboardRequest).
   */
  onFieldSelectionChange1(event: any) {
    let selectedFields = [...(event.value || [])];

    if (selectedFields.length > 1 && selectedFields.includes("All")) {
      selectedFields = selectedFields.filter((x) => x !== "All");
    }

    if (selectedFields.length === 1 && selectedFields[0] === "All") {
      this.previousSelectedFields = ["All"];
      this.resetToAll1();
      return;
    }

    const existingFilters = [...this.demographicFilters];

    this.demographicFilters = selectedFields.map((label) => {
      const existing = existingFilters.find((x) => x.fieldName === label);

      return existing
        ? existing
        : {
            fieldName: label,
            fieldValue: [],
          };
    });

    this.selectedFieldLabels = [...selectedFields];

    if (this.selectedFieldLabels.length === 0) {
      this.previousSelectedFields = ["All"];

      this.selectedFieldLabels = ["All"];
      this.activeFieldLabel = "All";
      this.activeFieldOptions = [];
      this.activeFieldSelectedValues = [];
      this.demographicFilters = [];

      return;
    }

    const newlyAdded = selectedFields.find(
      (x) => !this.previousSelectedFields.includes(x),
    );

    const activeLabel =
      newlyAdded ||
      (selectedFields.includes(this.activeFieldLabel)
        ? this.activeFieldLabel
        : selectedFields[selectedFields.length - 1]);

    this.previousSelectedFields = [...selectedFields];

    this.setActiveField1(activeLabel);
  }

  /**
   * Removes a field's chip entirely and falls back to the next active field
   * (or "All" if none remain) - engagement branch.
   */
  removeDemographicFilter1(filter: any) {
    this.demographicFilters = this.demographicFilters.filter(
      (x) => x.fieldName !== filter.fieldName,
    );

    this.appliedDemographicFilters = this.appliedDemographicFilters.filter(
      (x) => x.fieldName !== filter.fieldName,
    );

    this.selectedFieldLabels = this.selectedFieldLabels.filter(
      (x) => x !== filter.fieldName,
    );

    this.previousSelectedFields = [...this.selectedFieldLabels];

    if (this.selectedFieldLabels.length === 0) {
      this.resetToAll1();
      return;
    }

    const nextField =
      this.selectedFieldLabels[this.selectedFieldLabels.length - 1];

    this.setActiveField1(nextField);

    this.applyDemographicFilters1();
  }

  /**
   * Sends the accumulated demographicFilters (plus the currently active
   * field object) to the engagement dashboard API.
   */
  applyDemographicFilters1() {
    if (!this.activeFieldLabel || this.activeFieldLabel === "All") {
      return;
    }

    const currentField = this.activeFieldLabel;

    const activeFieldObject = this.getExcelResponse.formFields.find(
      (x) => x.label === currentField,
    );

    if (!activeFieldObject) {
      return;
    }

    const filters = this.demographicFilters
      .filter((x) => x.fieldValue && x.fieldValue.length > 0)
      .map((x) => ({
        fieldName: x.fieldName,
        fieldValue: [...x.fieldValue],
      }));

    this.appliedDemographicFilters = JSON.parse(JSON.stringify(filters));

    this.dashboardRequest = {
      surveyUId: this.surveyUid,
      formFields: [{ ...activeFieldObject }],
      demographicFilters: filters,
    };

    this.getDashBoardEngagementScoreDashBoard(this.dashboardRequest);

    const filtersForOptions = filters.filter(
      (x) => x.fieldName !== currentField,
    );

    this.getAvailableFilters1(filtersForOptions);
  }

  private setActiveField1(label: string) {
    this.activeFieldLabel = label;

    const filter = this.demographicFilters.find((x) => x.fieldName === label);

    this.activeFieldSelectedValues = filter ? [...filter.fieldValue] : [];
    // Field-specific custom order isn't persisted for external viewers,
    // so drop back to the default order whenever the active field changes.
    this.sortByTotal = false;

    const apiField = (this.availableDemographicFilters || []).find(
      (x) => x.fieldLabel === label,
    );

    if (apiField) {
      this.activeFieldOptions = [...apiField.availableValues];

      this.fieldOptionsCache[label] = [...apiField.availableValues];
    } else {
      this.activeFieldOptions = this.fieldOptionsCache[label]
        ? [...this.fieldOptionsCache[label]]
        : [];
    }
  }

  private resetToAll1() {
    this.selectedFieldLabels = ["All"];
    this.activeFieldLabel = "All";
    this.activeFieldOptions = [];
    this.activeFieldSelectedValues = [];
    this.demographicFilters = [];
    this.appliedDemographicFilters = [];
    this.sortByTotal = false;

    const allObject = this.getExcelResponse.formFields.find(
      (f) => f.label === "All",
    );

    this.dashboardRequest = {
      surveyUId: this.surveyUid,
      formFields: [allObject],
    };

    this.getDashBoardEngagementScoreDashBoard(this.dashboardRequest);

    this.getAvailableFilters1();
  }

  /**
   * Same as getAvailableFilters, but also refreshes select-2's options once
   * the response lands - matching the internal engagement dashboard's
   * behaviour (the participation branch's getAvailableFilters doesn't do
   * this refresh, so it's kept as a separate method rather than branching).
   */
  getAvailableFilters1(appliedFilters: any[] = []) {
    const payload = {
      surveyUId: this.surveyUid,
      appliedFilters,
    };

    this.surveyService
      .getAvailableDemographicFilters(payload)
      .subscribe((res: any) => {
        this.availableDemographicFilters = res || [];

        this.availableDemographicFilters.forEach((field) => {
          this.fieldOptionsCache[field.fieldLabel] = [...field.availableValues];
        });

        if (this.activeFieldLabel && this.activeFieldLabel !== "All") {
          this.setActiveField1(this.activeFieldLabel);
        }
      });
  }

  getDashBoardEngagementScoreDashBoard(obj) {
    this.surveyService
      .getEngagementDashboardWithFiltersForSurvey(obj)
      .subscribe((response: any) => {
        // Response wraps the dashboard in engScoreDashBoards[0] and carries
        // an Others-grouping note, same shape as the internal dashboard -
        // the previously-used getDashBoardForEngagementScore endpoint never
        // returns an Others note at all.
        const dashboard =
          (response.engScoreDashBoards && response.engScoreDashBoards[0]) ||
          {};

        this.getEngagementDashboardResponse = dashboard;
        this.otherFiltersNote = response.otherFilters || "";

        this.isSuppressed = !!response.isSuppressed;
        this.suppressionMessage =
          response.message ||
          "Sample size is too small to display this dashboard.";

        // Keep the Engagement Table tab in sync with whatever was just loaded.
        this.engagementTableResponse = dashboard;
        // Reflect whether this field already has a persisted custom order
        // instead of always showing the toggle as "off".
        const activeField = (
          (this.getExcelResponse && this.getExcelResponse.formFields) ||
          []
        ).find((f) => f.label === this.activeFieldLabel);
        this.sortByTotal = !!(activeField && activeField.useCustomOptionOrder);

        if (this.isSuppressed) {
          this.barChartLabels = [];
          this.barChartData = [];
          return;
        }

        this.buildEngagementBarChart(dashboard.engagementDatas || []);
      });
  }

  private buildEngagementBarChart(engagementDatas: any[]) {
    if (!engagementDatas || engagementDatas.length === 0) {
      this.barChartLabels = [];
      this.barChartData = [];
      return;
    }

    this.barChartLabels = engagementDatas.map((data) => data.filter);

    this.barChartData = [
      {
        data: engagementDatas.map(
          (data) => parseFloat((data.disengaged || "0%").replace("%", "")) || 0,
        ),
        label: "Disengaged",
        stack: "Stack 0",
        percentages: engagementDatas.map((data) => data.disengaged),
      },
      {
        data: engagementDatas.map(
          (data) =>
            parseFloat((data.crashBurners || "0%").replace("%", "")) || 0,
        ),
        label: "Crash & Burn",
        stack: "Stack 0",
        percentages: engagementDatas.map((data) => data.crashBurners),
      },
      {
        data: engagementDatas.map(
          (data) =>
            parseFloat((data.honeymoonHamsters || "0%").replace("%", "")) ||
            0,
        ),
        label: "Honeymooners & Hamsters",
        stack: "Stack 0",
        percentages: engagementDatas.map((data) => data.honeymoonHamsters),
      },
      {
        data: engagementDatas.map(
          (data) =>
            parseFloat((data.almostEngaged || "0%").replace("%", "")) || 0,
        ),
        label: "Almost Engaged",
        stack: "Stack 0",
        percentages: engagementDatas.map((data) => data.almostEngaged),
      },
      {
        data: engagementDatas.map(
          (data) =>
            parseFloat((data.fullyEngaged || "0%").replace("%", "")) || 0,
        ),
        label: "Engaged",
        stack: "Stack 0",
        percentages: engagementDatas.map((data) => data.fullyEngaged),
      },
    ];
  }

  verifyPassword() {
  let payload: any;

  if (this.isSurveyDashboard) {
    payload = {
      dashboardLinkId: this.dashboardLinkId,
      password: this.password,
    };
  } else {
    payload = {
      dashboardLinkId: this.engagementDashboardLinkId,
      password: this.password,
    };
  }

  this.surveyService.validateDashboardLinkPassword(payload).subscribe(
    (response: any) => {
      if (response === true || response.isValid) {
        this.showPasswordDialog = false;
        this.isPasswordVerified = true;

        if (this.isSurveyDashboard) {
          this.getDashBoard();
        } else {
          this.getEngagementDashBoard();
        }
      } else {
        this.passwordError = "Invalid password";
      }
    },
    () => {
      this.passwordError = "Invalid password";
    }
  );
}

  filterPopup(templateRef: TemplateRef<any>) {
    this.dialog.open(templateRef, {
      width: "500px",
    });
  }

  // Download the participation dashboard exactly as it is currently viewed —
  // the "All" view or whichever filter is selected on screen (this.selectedLabel).
  // No filter dropdown selection required.
  downloadParticipationCurrentView(type: string) {
    this.dialog.closeAll();
    if (!this.dashboardRequest) {
      return;
    }
    // Deep-clone so the download path can never mutate the live-view state.
    const request = JSON.parse(JSON.stringify(this.dashboardRequest));
    this.downloadParticipationDashBoardWithFilters(request, type);
  }

  // Download the engagement dashboard exactly as it is currently viewed —
  // the "All" view or whichever demographic filter is applied on screen.
  // Routes through the live-view request state, same as the participation
  // branch's downloadParticipationCurrentView.
  downloadEngagementCurrentView(type: string) {
    this.dialog.closeAll();
    if (!this.dashboardRequest) {
      return;
    }
    const request = JSON.parse(JSON.stringify(this.dashboardRequest));
    this.downloadEngagementDashBoardWithFilters(request, type);
  }

    applyParticipationFilters(type: string) {
    this.dialog.closeAll(); // close popup before download starts

    // Ignore the "Current View" sentinel when resolving explicit filter picks.
    const realSelected = (this.selectedFilterLabels || []).filter(
      (label) => label !== this.currentViewOption,
    );

    const selectedObjects = this.getPartipantExcelResponse.formFields.filter(
      (field) => field.usedForFilter && realSelected.includes(field.label),
    );

    // No specific filters chosen (or "Current View" selected) → download
    // exactly the on-screen view.
    if (selectedObjects.length === 0) {
      this.downloadParticipationCurrentView(type);
      return;
    }

    const request = {
      surveyUId: this.surveyUid,
      formFields: selectedObjects,
    };

    this.downloadParticipationDashBoardWithFilters(request, type);
  }

    downloadParticipationDashBoardWithFilters(request: any, type: string) {
    if (!request.formFields || request.formFields.length === 0) {
      const allObject = this.getPartipantExcelResponse.formFields.find(
        (field) => field.label === "All",
      );
      if (!allObject) return;
      request.formFields = [allObject];
    }

    const payload = {
      surveyUId: this.surveyUid,
      formFields: request.formFields,
      demographicFilters: request.demographicFilters,
    };

    this.isDownloading = true;

    this.surveyService
      .getDashboardWithFiltersForSurvey(payload)
      .subscribe({
        next: (res: any) => {
          this.otherFiltersNote = res.otherFilters || "";

          // Map each item in the array with surveyName and logo
          const reports = res.dashboardModels.map((item) => ({
            ...item,
            surveyName: this.getPartipantExcelResponse.surveyTitle,
            logo: this.getPartipantExcelResponse.clientLogo,
          }));

          if (type === "ppt") {
            this.pptParticipationService.downloadDashboardReport(
              reports,
              res.otherFilters,
              res.clientLogo,
            );
          } else if (type === "excel") {
            this.surveyService
              .downloadParticipationDashboardExcel(payload)
              .then((blob: Blob) => {
                this.surveyService.triggerFileDownload(
                  blob,
                  `${this.getPartipantExcelResponse.surveyTitle}_ParticipationDashboard.xlsx`,
                );
              })
              .catch((err) => console.error("Excel download failed", err));
          }

          this.isDownloading = false;
          this.dialog.closeAll();
        },
        error: (err) => {
          console.error("Download failed", err);
          this.isDownloading = false;
        },
      });
  }

  applyFilters(type: string) {
    this.dialog.closeAll(); // close popup before download starts

    // Ignore the "Current View" sentinel when resolving explicit filter picks.
    const realSelected = (this.selectedFilterLabels || []).filter(
      (label) => label !== this.currentViewOption,
    );

    const selectedObjects = this.getExcelResponse.formFields.filter(
      (field) => field.usedForFilter && realSelected.includes(field.label),
    );

    // No specific filters chosen (or "Current View" selected) → download
    // exactly the on-screen view.
    if (selectedObjects.length === 0) {
      this.downloadEngagementCurrentView(type);
      return;
    }

    const request = {
      surveyUId: this.surveyUid,
      formFields: selectedObjects,
    };

    this.downloadEngagementDashBoardWithFilters(request, type);
  }

  downloadEngagementDashBoardWithFilters(request: any, type: string) {
    if (!request.formFields || request.formFields.length === 0) {
      const allObject = this.getExcelResponse.formFields.find(
        (field) => field.label === "All",
      );
      if (!allObject) return;
      request.formFields = [allObject];
    }

    const payload = {
      surveyUId: this.surveyUid,
      formFields: request.formFields,
      demographicFilters: request.demographicFilters,
    };

    this.isDownloading = true;

    this.surveyService
      .getEngagementDashboardWithFiltersForSurvey(payload)
      .subscribe({
        next: (res: any) => {
          // Keep the Engagement Table tab in sync with whatever was just
          // downloaded, same as the internal dashboard.
          this.engagementTableResponse = res.engScoreDashBoards[0];
          this.otherFiltersNote = res.otherFilters || "";

          // Map each item in the array with surveyName and logo
          const reports = res.engScoreDashBoards.map((item) => ({
            ...item,
            surveyName: this.getExcelResponse.surveyTitle,
            logo: this.getExcelResponse.clientLogo,
          }));

          if (type === "ppt") {
            this.pptService.downloadEngagementDashboardReport(
              reports,
              res.otherFilters,
              res.clientLogo,
            );
          } else if (type === "excel") {
            this.surveyService
              .downloadEngagementDashboardExcel(payload)
              .then((blob: Blob) => {
                this.surveyService.triggerFileDownload(
                  blob,
                  `${this.getExcelResponse.surveyTitle}_EngagementDashboard.xlsx`,
                );
              })
              .catch((err) => console.error("Excel download failed", err));
          }

          this.isDownloading = false;
          this.dialog.closeAll();
        },
        error: (err) => {
          console.error("Download failed", err);
          this.isDownloading = false;
        },
      });
  }
}
